import { environment } from '../../../environments/environment';

/**
 * Env-configured minimums for browsers that can run this Angular PWA.
 * Safari is major.minor (`'17.6'`); others are integer majors.
 */
export type MinBrowserVersions = {
  chrome: number;
  edge: number;
  firefox: number;
  safari: string;
  brave: number;
};

/** Browsers in the allowlist — Angular can run here; version is checked next. */
export type SupportedBrowserFamily = keyof MinBrowserVersions;

/** Floors from the active environment file (overridable per build). */
export function envMinBrowserVersions(): MinBrowserVersions {
  return environment.minBrowserVersions;
}

// Public download pages — browsers refuse chrome:// / edge:// / about: from web content.
export const CHROME_UPDATE_URL = 'https://www.google.com/chrome/';
export const EDGE_UPDATE_URL = 'https://www.microsoft.com/edge';
export const FIREFOX_UPDATE_URL = 'https://www.firefox.com/';
export const SAFARI_UPDATE_URL = 'https://support.apple.com/HT204416';
export const BRAVE_UPDATE_URL = 'https://brave.com/download/';

/** Entry in `navigator.userAgentData.brands`. */
interface UserAgentBrand {
  brand?: string;
  version?: string;
}

/** The slice of `navigator` this gate reads, passed in so every rule is unit-testable. */
export interface BrowserVersionNavigator {
  userAgent?: string;
  userAgentData?: { brands?: UserAgentBrand[] };
}

function currentNavigator(): BrowserVersionNavigator {
  return typeof navigator !== 'undefined' ? (navigator as BrowserVersionNavigator) : {};
}

// GREASE brands ("Not A;Brand", "Not=A?Brand", …) vary only in punctuation, so compare on letters.
function isGreaseBrand(brand: string): boolean {
  return brand.toLowerCase().replace(/[^a-z]/g, '') === 'notabrand';
}

function realBrands(nav: BrowserVersionNavigator): { brand: string; version: string }[] {
  const brands = nav.userAgentData?.brands;
  if (!Array.isArray(brands)) return [];
  return brands.filter(
    (entry): entry is { brand: string; version: string } =>
      typeof entry?.brand === 'string' &&
      typeof entry?.version === 'string' &&
      !isGreaseBrand(entry.brand),
  );
}

function toMajor(version: string): number | null {
  const major = Number.parseInt(version.split('.')[0] ?? '', 10);
  return Number.isInteger(major) && major > 0 ? major : null;
}

/** Split a version string/number into numeric parts for pairwise compare. */
export function versionParts(version: string | number): number[] {
  if (typeof version === 'number') {
    return Number.isFinite(version) ? [version] : [];
  }
  return version
    .split('.')
    .map((part) => Number.parseInt(part, 10))
    .filter((n) => Number.isInteger(n));
}

/** True when `current` is strictly below `minimum` (missing current → below). */
export function isVersionBelow(
  current: string | number | null | undefined,
  minimum: string | number,
): boolean {
  if (current == null) return true;
  const cur = versionParts(current);
  const min = versionParts(minimum);
  if (cur.length === 0 || min.length === 0) return true;
  const len = Math.max(cur.length, min.length);
  for (let i = 0; i < len; i++) {
    const c = cur[i] ?? 0;
    const m = min[i] ?? 0;
    if (c < m) return true;
    if (c > m) return false;
  }
  return false;
}

/**
 * Chromium-based products that are *not* in `minBrowserVersions` (Opera, Vivaldi, …).
 * Their UAs still contain `Chrome/`, so without this check they would inherit
 * Chrome's install allowlist. They can still *run* the web app when
 * `blockBrowserAccess` is false (version-gated against the Chrome floor).
 */
function matchesRunOnlyChromiumShell(ua: string, brandNames: string[]): boolean {
  const brands = brandNames.join(' ');
  if (/OPR\/|Opera\//i.test(ua) || /\bopera\b/i.test(brands)) return true;
  if (/Vivaldi\//i.test(ua) || /\bvivaldi\b/i.test(brands)) return true;
  if (/SamsungBrowser\//i.test(ua) || /\bsamsung\b/i.test(brands)) return true;
  if (/YaBrowser\//i.test(ua) || /\byandex\b/i.test(brands)) return true;
  if (/UCBrowser\/|UCWEB/i.test(ua)) return true;
  if (/\barc\b/i.test(brands)) return true;
  return false;
}

/**
 * Which allowlisted family this is, or `null` when not in env `minBrowserVersions`
 * (includes run-only Chromium shells like Opera, and truly unsupported engines).
 * Order matters: Edge/Brave before Chrome because their UAs also contain `Chrome/`.
 * Non-allowlisted Chromium shells are rejected *before* the Chrome match so they
 * never inherit the install allowlist.
 */
export function browserFamily(
  nav: BrowserVersionNavigator = currentNavigator(),
): SupportedBrowserFamily | null {
  const brands = realBrands(nav);
  const ua = nav.userAgent ?? '';
  const brandNames = brands.map((entry) => entry.brand.toLowerCase());

  if (brandNames.includes('microsoft edge') || /Edg(?:A|iOS)?\//i.test(ua)) return 'edge';
  if (brandNames.includes('brave') || /\bBrave\//i.test(ua)) return 'brave';
  if (brandNames.includes('firefox') || /Firefox\//i.test(ua)) return 'firefox';
  // Safari: has Version/ and Safari/, but not Chrome/Chromium/Edg (iOS Chrome uses CriOS).
  if (
    /Safari\//i.test(ua) &&
    /Version\//i.test(ua) &&
    !/Chrome\/|Chromium\/|Edg\/|CriOS\/|FxiOS\//i.test(ua)
  ) {
    return 'safari';
  }
  // Opera / Vivaldi / … also ship `Chrome/` + a Chromium Client Hint — stop them here.
  if (matchesRunOnlyChromiumShell(ua, brandNames)) return null;
  if (
    brandNames.includes('google chrome') ||
    brandNames.includes('chromium') ||
    /Chrome\/|Chromium\/|CriOS\//i.test(ua)
  ) {
    return 'chrome';
  }
  return null;
}

/** True when this is a Chromium shell outside the env install allowlist (Opera, …). */
export function isRunOnlyChromiumShell(
  nav: BrowserVersionNavigator = currentNavigator(),
): boolean {
  if (browserFamily(nav) !== null) return false;
  const brands = realBrands(nav);
  const ua = nav.userAgent ?? '';
  const brandNames = brands.map((entry) => entry.brand.toLowerCase());
  return matchesRunOnlyChromiumShell(ua, brandNames);
}

/** Chromium major from Client Hints / UA — used for Chrome family and run-only shells. */
function readChromiumMajor(nav: BrowserVersionNavigator): number | null {
  const brands = realBrands(nav);
  const ua = nav.userAgent ?? '';
  const preferred =
    brands.find((entry) => entry.brand.toLowerCase() === 'chromium') ??
    brands.find((entry) => entry.brand.toLowerCase() === 'google chrome') ??
    brands.find((entry) => entry.brand.toLowerCase() === 'opera') ??
    brands.find((entry) => entry.brand.toLowerCase() === 'brave') ??
    brands[0];
  if (preferred) {
    const major = toMajor(preferred.version);
    if (major !== null) return major;
  }
  const match = /(?:Chrome|Chromium|CriOS)\/(\d+)/i.exec(ua);
  return match ? toMajor(match[1]) : null;
}

/**
 * This browser's reported version for the gate (major, or Safari major.minor),
 * or `null` when it can't be determined. Run-only Chromium shells report their
 * underlying Chromium major so they can be version-gated against the Chrome floor.
 */
export function browserVersion(
  nav: BrowserVersionNavigator = currentNavigator(),
): string | number | null {
  const family = browserFamily(nav);
  if (family === null) {
    return isRunOnlyChromiumShell(nav) ? readChromiumMajor(nav) : null;
  }

  const brands = realBrands(nav);
  const ua = nav.userAgent ?? '';

  if (family === 'safari') {
    const match = /Version\/(\d+(?:\.\d+)*)/i.exec(ua);
    return match?.[1] ?? null;
  }

  if (family === 'firefox') {
    const fromBrand = brands.find((entry) => entry.brand.toLowerCase() === 'firefox');
    if (fromBrand) {
      const major = toMajor(fromBrand.version);
      if (major !== null) return major;
    }
    const match = /Firefox\/(\d+)/i.exec(ua);
    return match ? toMajor(match[1]) : null;
  }

  if (family === 'edge') {
    const fromBrand =
      brands.find((entry) => entry.brand.toLowerCase() === 'microsoft edge') ??
      brands.find((entry) => entry.brand.toLowerCase() === 'chromium');
    if (fromBrand) {
      const major = toMajor(fromBrand.version);
      if (major !== null) return major;
    }
    const match = /Edg\/(\d+)/i.exec(ua);
    return match ? toMajor(match[1]) : null;
  }

  if (family === 'brave') {
    const fromBrand =
      brands.find((entry) => entry.brand.toLowerCase() === 'brave') ??
      brands.find((entry) => entry.brand.toLowerCase() === 'chromium');
    if (fromBrand) {
      const major = toMajor(fromBrand.version);
      if (major !== null) return major;
    }
  }

  return readChromiumMajor(nav);
}

/**
 * True when this engine cannot run the app at all (IE, unknown non-Chromium).
 * Run-only Chromium shells (Opera, …) return false — they are handled by the
 * install-unsupported / web-only branches instead.
 */
export function isUnsupportedBrowser(nav: BrowserVersionNavigator = currentNavigator()): boolean {
  return browserFamily(nav) === null && !isRunOnlyChromiumShell(nav);
}

/** Parse CMS `minChromiumMajor`; `null` when missing or malformed. */
export function parseCmsMinChromiumMajor(cmsMinChromiumMajor: unknown): number | null {
  const parsed =
    typeof cmsMinChromiumMajor === 'number'
      ? cmsMinChromiumMajor
      : typeof cmsMinChromiumMajor === 'string' && cmsMinChromiumMajor.trim() !== ''
        ? Number(cmsMinChromiumMajor)
        : Number.NaN;
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Effective floor for this family. CMS (AppConfig) is the source of truth; env is the default.
 *   - Firefox / Safari: env only — CMS `minChromiumMajor` never applies (Chromium-only).
 *   - Chromium (chrome / edge / brave): the CMS value when usable — it may raise *or lower*
 *     the env floor, so ops can admit an older Chrome (e.g. 100–109) without a
 *     deploy. Missing / malformed / CMS-timeout falls back to the env floor.
 *   Lowering below the build's `.browserslistrc` targets admits browsers the bundle was not
 *   compiled for — keep the two in step.
 */
export function resolveEffectiveMinVersion(
  family: SupportedBrowserFamily,
  cmsMinChromiumMajor: unknown = undefined,
  mins: MinBrowserVersions = envMinBrowserVersions(),
): string | number {
  const envFloor = mins[family];
  if (family === 'firefox' || family === 'safari') return envFloor;
  const cms = parseCmsMinChromiumMajor(cmsMinChromiumMajor);
  if (cms === null || typeof envFloor !== 'number') return envFloor;
  return cms;
}

/**
 * Whether this session must be sent to the update screen: allowlisted family
 * whose version is missing or below the effective floor (CMS when set, else env).
 * Run-only Chromium is checked separately via {@link shouldBlockRunOnlyChromiumVersion}.
 */
export function shouldBlockBrowserVersion(
  minimum?: string | number | null,
  nav: BrowserVersionNavigator = currentNavigator(),
  mins: MinBrowserVersions = envMinBrowserVersions(),
): boolean {
  const family = browserFamily(nav);
  if (family === null) return false;
  const floor = minimum ?? mins[family];
  return isVersionBelow(browserVersion(nav), floor);
}

/**
 * Version gate for run-only Chromium shells: compare underlying Chromium major
 * against the effective Chrome floor (CMS when set, else env).
 */
export function shouldBlockRunOnlyChromiumVersion(
  cmsMinChromiumMajor: unknown = undefined,
  nav: BrowserVersionNavigator = currentNavigator(),
  mins: MinBrowserVersions = envMinBrowserVersions(),
): boolean {
  if (!isRunOnlyChromiumShell(nav)) return false;
  const floor = resolveEffectiveMinVersion('chrome', cmsMinChromiumMajor, mins);
  return isVersionBelow(browserVersion(nav), floor);
}

/** Cap on waiting for CMS so a hung config can't stall the version gate (matches phone gate). */
export const BROWSER_VERSION_GATE_MAX_WAIT_MS = 4000;

/** Where the update button sends the user. */
export function browserUpdateUrl(nav: BrowserVersionNavigator = currentNavigator()): string {
  switch (browserFamily(nav)) {
    case 'edge':
      return EDGE_UPDATE_URL;
    case 'firefox':
      return FIREFOX_UPDATE_URL;
    case 'safari':
      return SAFARI_UPDATE_URL;
    case 'brave':
      return BRAVE_UPDATE_URL;
    case 'chrome':
    default:
      return CHROME_UPDATE_URL;
  }
}

/** Browser name for update copy. */
export function browserDisplayName(nav: BrowserVersionNavigator = currentNavigator()): string {
  switch (browserFamily(nav)) {
    case 'edge':
      return 'Edge';
    case 'firefox':
      return 'Firefox';
    case 'safari':
      return 'Safari';
    case 'brave':
      return 'Brave';
    case 'chrome':
    default:
      // Run-only Chromium (Opera, …) shares the Chrome floor copy.
      return 'Chrome';
  }
}

/** Display form of the env floor for the current (or given) family. */
export function formatMinVersion(
  family: SupportedBrowserFamily | null = browserFamily(),
  mins: MinBrowserVersions = envMinBrowserVersions(),
): string {
  if (family === null) return String(mins.chrome);
  return String(mins[family]);
}
